import { type FC } from "react";
import {
  Checkbox,
  Typography,
  Card,
  Grid,
  Box,
  Stack,
  IconButton,
  Link as MuiLink,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { useTranslation } from "react-i18next";
import type { PlatformMenuItemsListRow } from "@/partner/entities/platformMenuItem";
import {
  LINK_TARGET_OPTION_LABEL_KEYS,
  resolveLinkTargetOption,
} from "@/partner/features/links";

const StyledCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== "selected",
})<{ selected?: boolean }>(({ theme, selected }) => ({
  backgroundColor: selected
    ? theme.palette.action.selected
    : theme.palette.background.paper,
  padding: theme.spacing(2, 1),
  overflow: "visible",
  position: "relative",
}));

interface PlatformMenuListItemProps {
  item: PlatformMenuItemsListRow;
  index: number;
  selected: boolean;
  onSelectOneChange: (
    id: string,
    event: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onEditItem: (id: string) => void;
  onCreateItem: (index: number) => void;
  isLast: boolean;
  dragHandleProps?: any;
}

export const PlatformMenuListItem: FC<PlatformMenuListItemProps> = ({
  item,
  selected,
  onSelectOneChange,
  onEditItem,
  onCreateItem,
  isLast,
  dragHandleProps,
}) => {
  const { t } = useTranslation();

  const style = {
    opacity: item.publishAt ? 1 : 0.6,
  };

  const handleEdit = () => {
    onEditItem(item.id);
  };

  const handleCreateAbove = () => {
    onCreateItem(item.index);
  };

  const handleCreateBelow = () => {
    onCreateItem(item.index + 1);
  };

  const targetOption = resolveLinkTargetOption(item.targetType, item.targetUrl);

  return (
    <StyledCard selected={selected} style={style} elevation={0}>
      {/* Create button above */}
      <Box
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          top: "-20px",
          height: "32px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          opacity: 0,
          transition: "opacity 0.2s ease-in-out",
          "&:hover": {
            opacity: 1,
          },
        }}
      >
        <IconButton size="small" onClick={handleCreateAbove}>
          <AddCircleIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Create button below (only for last item) */}
      {isLast && (
        <Box
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: "-20px",
            height: "32px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            opacity: 0,
            transition: "opacity 0.2s ease-in-out",
            "&:hover": {
              opacity: 1,
            },
          }}
        >
          <IconButton size="small" onClick={handleCreateBelow}>
            <AddCircleIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      <Grid container spacing={2} wrap="nowrap" alignItems="flex-start">
        <Grid size="auto">
          <Checkbox
            color="default"
            checked={selected}
            onChange={(event) => onSelectOneChange(item.id, event)}
            onClick={(event) => event.stopPropagation()}
          />
        </Grid>

        <Grid size="grow">
          <Box
            sx={{
              ml: { xs: 1.5, md: 0 },
              mr: { xs: 1.5, md: 0 },
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box sx={{ flex: 1 }}>
                <MuiLink
                  underline="hover"
                  variant="h6"
                  color="textPrimary"
                  onClick={(e) => {
                    e.preventDefault();
                    handleEdit();
                  }}
                  sx={{
                    fontSize: { xs: "0.9rem", sm: "1.1rem", md: "1.2rem" },
                    cursor: "pointer",
                  }}
                >
                  {item.label}
                </MuiLink>

                <Box sx={{ mt: 0.5 }}>
                  {item.linkType === "INTERNAL" && item.targetType ? (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="body2" color="text.secondary">
                        {targetOption
                          ? t("linkTarget.label", {
                              type: t(
                                LINK_TARGET_OPTION_LABEL_KEYS[targetOption]
                              ),
                              title:
                                item.targetTitle ?? t("linkTarget.missing"),
                            })
                          : (item.targetTitle ?? t("linkTarget.missing"))}
                      </Typography>
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {item.href}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Stack>
          </Box>
        </Grid>

        <Grid size="auto" container justifyContent="flex-end">
          {item.publishAt ? (
            <Box>
              <Typography variant="body2" color="textPrimary" align="right">
                {t("platformMenu.item.publishedAt", { value: item.publishAt })}
              </Typography>
              <Typography variant="body2" color="textSecondary" align="right">
                {t("platformMenu.item.publishedDate", {
                  value: item.publishAt,
                })}
              </Typography>
            </Box>
          ) : (
            <Typography
              variant="body2"
              color="textPrimary"
              align="right"
              sx={{ pr: 2 }}
            >
              {t("platformMenu.item.notPublished")}
            </Typography>
          )}
        </Grid>

        <Grid size="auto">
          <Stack direction="row" alignItems="center" spacing={1}>
            {item.targetUrl && (
              <IconButton
                target="_blank"
                href={item.targetUrl}
                size="large"
                component="a"
              >
                <OpenInNewIcon />
              </IconButton>
            )}
            <IconButton {...dragHandleProps} size="large">
              <DragIndicatorIcon />
            </IconButton>
          </Stack>
        </Grid>
      </Grid>
    </StyledCard>
  );
};
